import { Home, LucideIcon } from "lucide-react";
import { CategoryButton, CategoryButtonSkeleton } from "@/components/CategoryButton";
import { Category, Subcategory } from "@/types/category";
import { getIconByName } from "@/utils/iconMapping";
import { memo, useCallback } from "react";
import { validateCategoryData, safeString } from "@/utils/reactSafety";
import { MobileErrorBoundary } from "@/components/ui/mobile-error-boundary";

interface CategoryListProps {
  loading: boolean;
  categories: Category[];
  subcategories: { [key: number]: Subcategory[] };
  activeCategory: string;
  expandedCategory: string | null;
  activeSubcategoryId: number | null;
  iconMapping: { [key: string]: LucideIcon };
  onCategoryClick: (category: string) => void;
  onSubcategoryClick: (subcategory: Subcategory) => void;
}

export const CategoryList = memo(({
  loading,
  categories,
  subcategories,
  activeCategory,
  expandedCategory,
  activeSubcategoryId,
  iconMapping,
  onCategoryClick,
  onSubcategoryClick,
}: CategoryListProps) => {
  const getIconComponent = useCallback((iconName: string | null): LucideIcon => {
    if (!iconName) {
      return Home;
    }
    
    // Use our utility function to get the icon
    const iconComponent = getIconByName(iconName);
    if (iconComponent) {
      return iconComponent;
    }
    
    return Home;
  }, []);

  // Handler for clicking "All [Category]" subcategory option
  const handleViewAllClick = useCallback((categoryId: number, categoryLabel: string) => {
    // Clear the subcategory selection but keep the category active
    onCategoryClick(categoryLabel);
    // Reset the active subcategory ID by passing null
    const resetSubcategory: Subcategory = {
      id: -1, // Using -1 as a special value to indicate "All"
      label: `All ${categoryLabel}`,
      parent_category: categoryId,
      created_at: new Date().toISOString(),
      display_order: -1
    };
    
    onSubcategoryClick(resetSubcategory);
  }, [onCategoryClick, onSubcategoryClick]);

  if (loading) {
    return (
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {[...Array(5)].map((_, index) => (
          <CategoryButtonSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <div className="text-gray-500 text-center py-8">
        No categories found
      </div>
    );
  }

  // Sort categories by their order field if available
  const sortedCategories = [...categories].sort((a, b) => {
    // If both have order, sort by order
    if (a.order !== null && b.order !== null && a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    // If only one has order, prioritize the one with order
    if (a.order !== null && a.order !== undefined) return -1;
    if (b.order !== null && b.order !== undefined) return 1;
    // If neither has order, keep original order
    return 0;
  });

  return (
    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
      {sortedCategories.map((category) => {
        // Validate category data before rendering
        if (!validateCategoryData(category)) {
          console.error('Skipping invalid category:', category);
          return null;
        }

        const IconComponent = getIconComponent(category.icon);
        
        // Sort subcategories by display_order if available
        const categorySubcategories = subcategories[category.id] || [];
        const sortedSubcategories = [...categorySubcategories].sort((a, b) => {
          // If both have display_order, sort by it
          if (a.display_order !== null && b.display_order !== null && 
              a.display_order !== undefined && b.display_order !== undefined) {
            return a.display_order - b.display_order;
          }
          // If only one has display_order, prioritize the one with display_order
          if (a.display_order !== null && a.display_order !== undefined) return -1;
          if (b.display_order !== null && b.display_order !== undefined) return 1;
          // If neither has display_order, keep original order
          return 0;
        }).filter(subcategory => subcategory && typeof subcategory === 'object');
        
        return (
          <MobileErrorBoundary key={category.id} componentName="CategoryButton">
            <div className="space-y-1.5">
              <CategoryButton
                icon={IconComponent}
                label={safeString(category.label)}
                isActive={activeCategory === safeString(category.label)}
                isExpanded={expandedCategory === safeString(category.label)}
                onClick={() => onCategoryClick(safeString(category.label))}
              />
              {expandedCategory === safeString(category.label) && sortedSubcategories.length > 0 && (
                <div className="bg-white rounded-xl p-1.5 shadow-sm space-y-0.5">
                  {/* Add "All [Category]" option */}
                  <button
                    onClick={() => handleViewAllClick(category.id, safeString(category.label))}
                    className={`w-full text-left px-2 py-1.5 text-xs font-medium rounded-lg transition-colors bg-primary-light text-primary-dark hover:bg-primary-light hover:text-primary`}
                  >
                    All {safeString(category.label)}
                  </button>
                  
                  {/* Original subcategory list with sorted items */}
                  {sortedSubcategories.map((subcategory) => (
                    <button
                      key={subcategory.id}
                      onClick={() => onSubcategoryClick(subcategory)}
                      className={`w-full text-left px-2 py-1.5 text-xs rounded-lg transition-colors ${
                        activeSubcategoryId === subcategory.id
                          ? "bg-primary text-white"
                          : "hover:bg-primary-light hover:text-primary"
                      }`}
                    >
                      {safeString(subcategory.label)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </MobileErrorBoundary>
        );
      }).filter(Boolean)}
    </div>
  );
});

CategoryList.displayName = "CategoryList";
